import { Description } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button, Divider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import CropMonitoringDetail from '/@/components/crop-monitoring/crop-monitoring-detail';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { getAppInstance } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking/actions';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CornCropMonitoring, CropMonitoring } from '/@/types/crop-monitoring';
import { PHENOLOGY_LABEL_KEYS } from '/@/types/crop-monitoring';
import { getCropMonitoringData, isFallowPeriod } from '/@/utils/crop-monitoring';
import { capitalizeFirstLetter, formatDate } from '/@/utils/index';
import { reverse } from '/@/utils/router';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

type TrackingContext = 'field-list' | 'crop-monitoring-list';

const getStatusText = (cropMonitoring: CropMonitoring): string | undefined => {
    if (isFallowPeriod(cropMonitoring.plantingDate)) {
        return _('Barbecho');
    }

    const cropData = getCropMonitoringData(cropMonitoring);
    if (!cropData) return;

    const phenology = cropData?.state?.phenology;
    const vegetativeStage = cropData?.state?.vegetativeStage;

    if (phenology === 'emergence' && vegetativeStage) {
        return `V${vegetativeStage}`;
    }

    if (!phenology) {
        return _('Siembra');
    }

    return _(PHENOLOGY_LABEL_KEYS[phenology]);
};

const getPhenologyStyles = (cropMonitoring: CropMonitoring) => {
    const cropData = getCropMonitoringData(cropMonitoring);
    if (!cropData) return styles[`cm-${cropMonitoring?.crop}`];

    const phenology = cropData?.state?.phenology;
    const vegetativeStage = cropData?.state?.vegetativeStage;

    switch (phenology) {
        case 'maturity':
            return styles.maturity;
        case 'flowering':
            return styles.flowering;
        case 'emergence':
            return !vegetativeStage ? styles.emergence : styles[`v-${vegetativeStage}`];
        case 'harvested':
            return styles.harvested;
        default:
            return styles[`v-${vegetativeStage || 'seed'}`];
    }
};

// Navigation helpers
const useNavigation = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const trackAndNavigate = (
        trackingKey: string,
        cropMonitoringId: number,
        route: string,
        eventName: 'Crop Monitoring opened' | 'Crop Monitoring report opened',
        context: TrackingContext
    ) => {
        console.log(eventName, context);
        mpTrackEvent(eventName, { entry_source: context });
        dispatch(trackEvent({ key: trackingKey, id: cropMonitoringId }));
        navigate(route);
    };

    return { trackAndNavigate };
};

// Sub-components
const TextDisplay = ({
    isHomeSidebar,
    text,
    subtitle,
}: {
    isHomeSidebar: boolean;
    text: string;
    subtitle?: string;
}) => (
    <Stack justifyContent='center' alignItems='center' width='100%' height='100%'>
        <Typography
            color='primary'
            sx={{
                fontSize: isHomeSidebar ? '15px' : '27px',
                fontStyle: 'italic',
            }}
        >
            {text}
        </Typography>
        <Typography
            color='primary.mui.inactive'
            textAlign='center'
            sx={{
                fontSize: '10px',
                fontWeight: 400,
            }}
        >
            {subtitle}
        </Typography>
    </Stack>
);

const HarvestedSummary = ({
    cropData,
    cropMonitoring,
    onNavigateToReport,
    onNavigateToDetail,
}: {
    cropData: CornCropMonitoring;
    cropMonitoring: CropMonitoring;
    onNavigateToReport: () => void;
    onNavigateToDetail: () => void;
}) => {
    const appInstance = useSelector(getAppInstance);

    return (
        <Stack mt='-8px' spacing={1}>
            <Stack direction='row' spacing={1}>
                <Typography color='primary' fontWeight='bold'>
                    {_('Estado actual:')}
                </Typography>
                <Typography color='primary'>{_('Cosechado')}</Typography>
            </Stack>
            <Stack direction='row' spacing={1}>
                <Typography color='primary' fontWeight='bold'>
                    {_('Fecha de cosecha:')}
                </Typography>
                <Typography color='primary'>{formatDate(new Date(cropData?.expectedHarvestDate))}</Typography>
            </Stack>

            {cropMonitoring.result && appInstance.enableCropMonitoringReport && (
                <Button
                    variant='text'
                    color='secondary'
                    startIcon={<Description />}
                    sx={{ width: 'fit-content' }}
                    onClick={onNavigateToReport}
                >
                    {_('Ver reporte de cosecha')}
                </Button>
            )}
            {cropMonitoring.active && (
                <Button variant='contained' onClick={onNavigateToDetail} sx={{ width: 'fit-content' }}>
                    {_('Ver seguimiento del lote')}
                </Button>
            )}
        </Stack>
    );
};

const DetailGrid = ({ children, isMobile }: { children: React.ReactNode; isMobile: boolean }) => (
    <Grid
        container
        sx={{
            display: 'flex',
            alignItems: isMobile ? 'center' : 'end',
            borderBottom: '0.5px solid #E1E1E1',
            p: 1,
            width: '100%',
            height: '100%',
            alignContent: 'center',
        }}
    >
        {children}
    </Grid>
);

interface CardProps {
    cropMonitoring: CropMonitoring;
    index: number;
    isMobile: boolean;
    hideFieldName?: boolean;
    trackingKey?: string;
    isHomeSidebar?: boolean;
}

const Card = ({
    cropMonitoring,
    index,
    isMobile,
    hideFieldName = false,
    trackingKey,
    isHomeSidebar = false,
}: CardProps) => {
    const { trackAndNavigate } = useNavigation();
    const appInstance = useSelector(getAppInstance);

    // Derived data
    const isFallow = isFallowPeriod(cropMonitoring.plantingDate);
    const cropData = getCropMonitoringData(cropMonitoring);
    const statusText = getStatusText(cropMonitoring);
    const plantingDate = formatDate(new Date(cropMonitoring.plantingDate));
    const created = formatDate(new Date(cropMonitoring.created));
    const context: TrackingContext = isHomeSidebar ? 'field-list' : 'crop-monitoring-list';

    // Navigation handlers
    const navigateToFieldDetail = () => {
        const defaultTrackingKey = trackingKey || 'cornCropMonitoring.detail';
        const route =
            settings.app?.theme === 'expertobayer' && cropMonitoring.active
                ? reverse('fields.FieldDetail', { params: { fieldId: cropMonitoring.field } })
                : reverse('cropMonitoring.CornCropMonitoringDetail', {
                      params: { cropMonitoringId: cropMonitoring.id },
                  });

        trackAndNavigate(defaultTrackingKey, cropMonitoring.id, route, 'Crop Monitoring opened', context);
    };

    const navigateToFieldReport = () => {
        const defaultTrackingKey = trackingKey || 'cornCropMonitoring.detail';
        const route = reverse('cropMonitoring.CornCropMonitoringDetail.report', {
            params: { cropMonitoringId: cropMonitoring.id },
        });

        trackAndNavigate(defaultTrackingKey, cropMonitoring.id, route, 'Crop Monitoring report opened', context);
    };

    // Special case for harvested crops in expertobayer theme
    const shouldShowHarvestedSummary =
        settings.app?.theme === 'expertobayer' &&
        cropMonitoring.active &&
        cropData?.state?.phenology === 'harvested' &&
        isHomeSidebar;

    if (shouldShowHarvestedSummary && cropData) {
        return (
            <HarvestedSummary
                cropData={cropData}
                cropMonitoring={cropMonitoring}
                onNavigateToReport={navigateToFieldReport}
                onNavigateToDetail={navigateToFieldDetail}
            />
        );
    }

    const getImage = () => {
        if (isFallow) {
            return (
                <TextDisplay isHomeSidebar={isHomeSidebar} text={_('Barbecho')} subtitle={_('Período de descanso')} />
            );
        }
        if (!cropData) {
            return <TextDisplay isHomeSidebar={isHomeSidebar} text={capitalizeFirstLetter(cropMonitoring?.crop)} />;
        }
        return (
            <>
                {isMobile &&
                    cropData?.state?.phenology === 'emergence' &&
                    Boolean(cropData?.state?.vegetativeStage) && (
                        <div className={classNames(styles.tag, styles[`tag-${settings.app?.theme || ''}`])}>
                            {`V${cropData?.state?.vegetativeStage}`}
                        </div>
                    )}
                <div className={classNames(styles.plants, getPhenologyStyles(cropMonitoring))} />
            </>
        );
    };

    return (
        <Stack
            direction='column'
            divider={<Divider />}
            sx={{
                border: '0.5px solid #E1E1E1',
                borderRadius: '10px',
                ...(!cropMonitoring.active && {
                    filter: 'grayscale(0.7)',
                }),
            }}
        >
            {!hideFieldName && (
                <Stack direction='row' divider={<Divider orientation='vertical' flexItem />} p={1} spacing={1}>
                    <Title size='sm' bolder theme={settings.app?.theme}>
                        #{index}
                    </Title>
                    <Title size='sm' bolder color='dark'>
                        {cropMonitoring.fieldName}
                    </Title>
                </Stack>
            )}

            <Stack direction='row' divider={<Divider orientation='vertical' flexItem />} height='100%'>
                <Stack sx={{ width: '35%' }} alignItems='flex-end' justifyContent='center' divider={<Divider />}>
                    {getImage()}
                </Stack>

                <Stack sx={{ width: '65%' }} justifyContent='space-between'>
                    {statusText && (
                        <DetailGrid isMobile={isMobile}>
                            <Grid item>
                                <CropMonitoringDetail icon='schedule' title={_('Estado')} data={statusText} color />
                            </Grid>
                        </DetailGrid>
                    )}

                    <DetailGrid isMobile={isMobile}>
                        <CropMonitoringDetail icon='event' title={_('Siembra')} data={plantingDate} />
                    </DetailGrid>

                    <DetailGrid isMobile={isMobile}>
                        <CropMonitoringDetail icon='event' title={_('Creación')} data={created} />
                    </DetailGrid>

                    <DetailGrid isMobile={isMobile}>
                        <CropMonitoringDetail
                            icon='spoke'
                            title={_('Producto')}
                            data={
                                cropData?.hasGenericProduct ? _('Híbrido Genérico') : cropMonitoring?.productName || ''
                            }
                        />
                    </DetailGrid>

                    {!isFallow && (
                        <DetailGrid isMobile={isMobile}>
                            <CropMonitoringDetail icon='eco' title={_('GDU Actual')} data={cropData?.accumulatedGdu} />
                        </DetailGrid>
                    )}

                    {cropMonitoring.result && appInstance.enableCropMonitoringReport && (
                        <Grid item sx={{ width: '100%' }}>
                            <Button
                                variant='text'
                                color='secondary'
                                startIcon={<Description />}
                                onClick={navigateToFieldReport}
                                sx={{ width: '100%' }}
                            >
                                {_('Ver reporte de cosecha')}
                            </Button>
                        </Grid>
                    )}

                    {cropMonitoring.active && (
                        <Grid item sx={{ width: '100%' }}>
                            <Button
                                variant='contained'
                                onClick={navigateToFieldDetail}
                                endIcon={<ArrowForwardIcon />}
                                sx={{ width: '100%' }}
                            >
                                {_('Ver seguimiento')}
                            </Button>
                        </Grid>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

export default Card;
